'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { CheckCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Button from '@/components/atoms/button';
import { ProductSuggestion } from '@/components/organisms/search/types';

interface UnmatchedRowProps {
  /** The raw code from the uploaded file that didn't resolve. Empty when the parser could not read one. */
  code: string;
  /**
   * Quantity parsed for that row — carried over when the fix is added.
   *
   * EWM-949 round 1 (R1-1): `0` (or anything below 1) means the parser could
   * NOT read a quantity from the file. It is never a number the customer typed,
   * and it must never be sent as a `count` — see `handleAdd` below.
   */
  quantity: number;
  /**
   * EWM-949 — the row's 1-based line number in the uploaded file, so a rejected
   * row can be located in the customer's spreadsheet (AC-5). It is also the only
   * handle on a row whose code cell was empty.
   */
  line?: number;
  /**
   * EWM-949 — why this row was rejected. Defaults to "SKU not found", which is
   * the truth ONLY for a row the catalogue lookup rejected; a row the parser
   * rejected (bad delimiter, no quantity, empty code) has its own reason and
   * must not be blamed on the catalogue.
   */
  reason?: string;
  addItemDisabled?: boolean;
  searchProducts?: (query: string) => Promise<ProductSuggestion[]>;
  addItem?: (lineItems: Array<{ sku: string; count: number }>) => Promise<object>;
}

// EWM-605 — a single unmatched upload row in the partial-success view. The
// user searches the catalogue (autocomplete) for the product they meant, picks
// it, and adds it to the cart with the row's original quantity. Each row
// resolves independently — the valid rows were already added in bulk.
const UnmatchedRow = ({
  code,
  quantity,
  line,
  reason,
  addItemDisabled,
  searchProducts,
  addItem,
}: UnmatchedRowProps) => {
  const translate = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const [query, setQuery] = useState(code);
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selected, setSelected] = useState<ProductSuggestion | null>(null);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [addError, setAddError] = useState('');

  // EWM-949 round 1 (R1-1) — a row the PARSER rejected has no usable quantity,
  // and this fix flow used to send the parser's `0` straight to the cart:
  // `quickOrder/addItems` refuses a non-positive count by putting the row in
  // `failed[]` while STILL answering `success: true`, so the row rendered the
  // green "Added" state having added nothing. That is the unearned success this
  // ticket exists to remove, one screen later. The customer supplies the
  // missing number here — it is not defaulted, because a row that says `0` in
  // the file is a number they chose, and adding 1 would order a unit nobody
  // asked for.
  const quantityMissing = !(quantity > 0);
  const [quantityInput, setQuantityInput] = useState('');
  const enteredQuantity = /^\d+$/.test(quantityInput.trim()) ? parseInt(quantityInput.trim(), 10) : 0;
  const addQuantity = quantityMissing ? enteredQuantity : quantity;

  // EWM-949 round 2 (R2-2) — the disabled Add has to SAY why. A sighted
  // customer reads the requirement off the empty box; a screen-reader customer
  // got a disabled button and silence, and an entered `0`, `-1` or `1.5` was
  // refused with nothing announced. The hint is associated with both the field
  // and the button from the first render, so the requirement is available
  // before the button is reached; when the value typed cannot be used the same
  // element carries the reason and the field is marked `aria-invalid`.
  const quantityHintId = useId();
  const quantityInvalid = quantityMissing && quantityInput.trim() !== '' && enteredQuantity < 1;

  // EWM-949 round 3 (N2) — the Add button's description has to name the reason
  // it is actually disabled. It used to say "enter a quantity" for every
  // blocked state, so a view-only customer was told to fill a box for a button
  // their role can never enable, and a customer who had not picked a product
  // yet was pointed at the wrong control.
  //
  // `addItem` missing is the same lived experience as the view-only role (the
  // button exists and can never fire), so it reads the same sentence. The
  // remaining two blockers are NOT exclusive — on first paint both are true —
  // and `aria-describedby` takes a list, so both are named in the order the
  // customer has to resolve them: pick the product, then give the quantity.
  const addUnavailableId = useId();
  const selectFirstId = useId();
  const addUnavailable = !!addItemDisabled || !addItem;
  const addBlockedIds = addUnavailable
    ? [addUnavailableId]
    : [!selected ? selectFirstId : undefined, quantityMissing ? quantityHintId : undefined].filter(Boolean);

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  // Debounced catalogue search. A selected product suppresses the search so
  // picking a suggestion doesn't immediately re-open the dropdown.
  //
  // EWM-760: verified NOT behavior-preserving to hoist this effect's setState
  // calls out via P-key-reset (adjust-state-during-render on any
  // query/searchProducts/selected change, matching this effect's dep array +
  // branches exactly). That refactor made `open`/`searching` true in the SAME
  // commit as mount instead of one effect-flush later, which broke a real,
  // currently-passing test (quick-order-upload.spec.tsx "lets the user fix an
  // unmatched row via autocomplete and add it to cart": findByLabelText then
  // matches BOTH the input and the now-simultaneously-rendered
  // `<ul aria-label>` listbox, because the loading dropdown is open from the
  // very first paint instead of a tick later). This is a genuine external-
  // system sync (debounced catalogue search) where the "start searching" UI
  // prep is inseparable from the effect's own post-mount timing — disabling
  // here rather than ship that regression.
  useEffect(() => {
    if (!searchProducts || selected) return;
    const term = query.trim();
    if (!term) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- EWM-760: debounced catalogue-search effect; see comment above the effect — hoisting this out changes real mount/update timing (verified via a breaking existing test), not a safe P-key-reset here.
      setSuggestions([]);
      setSearched(false);
      return;
    }
    let active = true;
    setSearching(true);
    // Open immediately so the loading row is visible on the first search too
    // (not only on subsequent ones once `open` is already true).
    setOpen(true);
    setActiveIndex(-1);
    const handle = setTimeout(async () => {
      try {
        const results = await searchProducts(term);
        if (!active) return;
        setSuggestions(results);
        setOpen(true);
      } finally {
        if (active) {
          setSearching(false);
          setSearched(true);
        }
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [query, searchProducts, selected]);

  // Close the dropdown when clicking outside the row.
  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const handleSelect = (product: ProductSuggestion) => {
    setSelected(product);
    setQuery(product.sku);
    setOpen(false);
    setSuggestions([]);
    setAddError('');
  };

  const handleAdd = async () => {
    // A count below 1 is never sent. The button is disabled in that state too;
    // this is the guard that makes it true of the function, not just the UI.
    if (!selected || !addItem || addQuantity < 1) return;
    setAdding(true);
    setAddError('');
    try {
      const res = (await addItem([{ sku: selected.sku, count: addQuantity }])) as
        { success?: boolean; message?: string; failed?: Array<{ sku?: string; reason?: string }> } | undefined;
      if (res && res.success === false) {
        setAddError(res.message || translate('common.something-went-wrong'));
        return;
      }
      // EWM-949 round 1 (R1-1) — `success: true` is NOT "this row went in".
      // `quickOrder/addItems` reports per-row refusals (unreadable count, no
      // price for this dealer, unavailable) in `failed[]` and still resolves
      // successfully. Reading only `success` is how a refused row came to show
      // "Added". The Quick Order accordion already honours `failed[]`; this
      // matches it for the row this flow adds.
      //
      // PR #1170 review — matched by PRESENCE, not by SKU equality. This flow
      // sends exactly one item, so any entry in `failed[]` is that item. The
      // previous `f.sku.toLowerCase() === selected.sku.toLowerCase()` test let
      // an entry whose `sku` is absent, or normalised differently upstream,
      // fall through to the green "Added" — precisely the failure this guard
      // exists to prevent.
      if (res?.failed?.length) {
        setAddError(translate('quick-order.unmatched-add-failed'));
        return;
      }
      setAdded(true);
    } catch {
      setAddError(translate('common.something-went-wrong'));
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && open && activeIndex >= 0 && activeIndex < suggestions.length) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const dropdownVisible = open && (searching || suggestions.length > 0 || searched);

  if (added && selected) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
        <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-13 truncate font-semibold text-gray-800">{selected.name}</p>
          <p className="text-12 text-gray-500">
            {translate('common.cat-no')}: {selected.sku} · {translate('quick-order.qty-column')} {addQuantity}
          </p>
        </div>
        <span className="text-12 ml-auto font-semibold text-green-700">{translate('quick-order.unmatched-added')}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
      {/* PR #1170 review (suggestion 2) — the reason is now a full sentence
          (up to ~87 chars: "This product could not be added to your cart — it
          may not be available on your account."), not the 13-char "SKU not
          found" this row was built for. Opposite a `min-w-0` SKU with the
          reason `shrink-0`, a long reason kept its max-content width and
          collapsed the SKU column inside the ~448px drawer. Give the reason its
          own line so it wraps in full width instead of fighting the SKU. */}
      <div className="mb-2">
        <div className="flex items-baseline gap-2">
          <span className="text-13 font-semibold text-gray-800">{code}</span>
          {line !== undefined && (
            <span className="text-12 text-gray-500">{translate('quick-order.row-line', { line })}</span>
          )}
        </div>
        <p className="text-12 mt-0.5 text-amber-700">{reason ?? translate('quick-order.sku-not-found')}</p>
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon
              className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              role="combobox"
              aria-expanded={dropdownVisible}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
              // EWM-949 (PR #1170 review) — "search for the product you meant
              // and choose it from the results" was described ONLY from the Add
              // button. The Button atom uses the native `disabled` attribute, so
              // in that state the element is not focusable and the text is
              // unreachable in focus mode — the one instruction that unblocks
              // the row could not be heard. It is also describing THIS input, so
              // it belongs here. The button keeps its copy too (it is still
              // correct when read in browse mode); the quantity hint already
              // works this way.
              aria-describedby={!addUnavailable && !selected ? selectFirstId : undefined}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value.toUpperCase());
                setSelected(null);
                setAddError('');
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setOpen(true)}
              placeholder={translate('quick-order.unmatched-search-placeholder')}
              aria-label={translate('quick-order.unmatched-search-placeholder')}
              disabled={!searchProducts}
              className="text-13 focus:border-primary focus:ring-primary/20 w-full rounded-lg border border-gray-200 bg-white py-2.5 pr-3 pl-9 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50"
            />
          </div>
          {/* EWM-949 round 1 (R1-1) — shown only when the file carried no usable
              quantity for this row. The add stays unavailable until the
              customer supplies one, so nothing is invented on their behalf and
              `count: 0` can never reach the BFF. */}
          {quantityMissing && (
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={quantityInput}
              onChange={(e) => {
                setQuantityInput(e.target.value);
                setAddError('');
              }}
              required
              aria-label={translate('quick-order.unmatched-quantity-label')}
              aria-invalid={quantityInvalid}
              aria-describedby={quantityHintId}
              placeholder={translate('quick-order.qty-column')}
              className={`text-13 focus:ring-primary/20 w-20 shrink-0 rounded-lg border bg-white px-3 py-2.5 focus:ring-2 focus:outline-none ${
                quantityInvalid ? 'border-red-500 focus:border-red-500' : 'focus:border-primary border-gray-200'
              }`}
            />
          )}
          <Button
            variant="primary"
            size="s"
            loading={adding}
            disabled={!selected || addItemDisabled || !addItem || addQuantity < 1}
            aria-describedby={addBlockedIds.length > 0 ? addBlockedIds.join(' ') : undefined}
            onClick={handleAdd}
            className="shrink-0 !rounded-lg !px-3 !font-semibold"
          >
            {translate('quick-order.add-to-cart')}
          </Button>
        </div>

        {dropdownVisible && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label={translate('quick-order.unmatched-search-placeholder')}
            className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          >
            {searching && (
              <li className="text-12 px-3 py-2 text-gray-400">{translate('quick-order.unmatched-searching')}</li>
            )}
            {!searching && suggestions.length === 0 && searched && (
              <li className="text-12 px-3 py-2 text-gray-500">{translate('quick-order.unmatched-no-results')}</li>
            )}
            {!searching &&
              suggestions.map((product, index) => (
                <li key={product.sku}>
                  <button
                    type="button"
                    role="option"
                    id={optionId(index)}
                    aria-selected={index === activeIndex}
                    onClick={() => handleSelect(product)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`block w-full px-3 py-2 text-left ${index === activeIndex ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                  >
                    <span className="text-13 block truncate font-medium text-gray-800">{product.name}</span>
                    <span className="text-12 text-gray-500">
                      {translate('common.cat-no')}: {product.sku}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* EWM-949 round 3 (R3-2) — the reason an entered quantity cannot be used
          has to be ANNOUNCED, not merely marked. `aria-invalid` flipping and
          this element's text changing are both silent while focus stays in the
          field, and the disabled Add button is skipped by Tab, so a
          screen-reader customer could traverse the entire row without ever
          learning why it would not add.

          `role="status"` (a polite live region), not the `role="alert"` the
          sign-in and PO-number fields use: those render an error-ONLY element
          that mounts when the error appears, whereas this one element is
          present from first paint carrying the hint, and an assertive region
          would announce that hint on mount. A live region is not announced for
          its initial content — only for a change — so this fires on exactly the
          hint↔reason transitions, in both directions, and not on a keystroke
          that leaves the text as it was. */}
      {quantityMissing && (
        <p
          id={quantityHintId}
          role="status"
          className={`text-12 mt-2 ${quantityInvalid ? 'text-red-500' : 'text-gray-600'}`}
        >
          {quantityInvalid
            ? translate('quick-order.unmatched-quantity-invalid')
            : translate('quick-order.unmatched-quantity-hint')}
        </p>
      )}

      {/* N2 — the other two blockers have no visible copy to point at (the
          empty search box and the view-only banner above the list already say
          it on screen), so they are described for assistive technology only.
          Not live regions: they are read from the button, on demand. */}
      {addUnavailable && (
        <p id={addUnavailableId} className="sr-only">
          {translate('cart.view-only-desc')}
        </p>
      )}
      {!addUnavailable && !selected && (
        <p id={selectFirstId} className="sr-only">
          {translate('quick-order.unmatched-select-first')}
        </p>
      )}

      {/* EWM-949 (PR #1170 review) — this is the most important message the row
          can produce ("this product could not be added to your cart") and it
          was the only one not announced: round 3 gave the quantity HINT a
          polite live region and left the failure silent. `role="alert"` is
          right here where `role="status"` was right there — a failed add is
          the customer's action not working, not an advisory. */}
      {addError && (
        <p className="text-12 mt-2 text-red-500" role="alert">
          {addError}
        </p>
      )}
    </div>
  );
};

export default UnmatchedRow;
